import { useState, useMemo, useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import { useTranslation } from '@/i18n'
import { useTaxonomy } from './TaxonomyContext'
import type { Taxonomy } from '@/config/classification'

type PanelConfig = { category: string; subcategory: string; type: string; label: string; note: string; annotationText: string; shape: 'rectangle' | 'polygon' | 'line' | 'point' }
const EMPTY_CONFIG: PanelConfig = { category: '', subcategory: '', type: '', label: '', note: '', annotationText: '', shape: 'rectangle' }

/**
 * ENG-2631: a persisted selection can go stale — the taxonomy may have changed, or
 * the tuple may be internally impossible (e.g. Equipment ▸ Valve). Drop any level
 * that isn't a valid child of its parent so the seed the panel *shows* is exactly
 * the seed it *publishes* — no blank dropdown hiding an invalid Category/Sub/Type.
 */
function sanitizeConfig(cfg: PanelConfig, taxonomy: Taxonomy): PanelConfig {
  const category = taxonomy.levelIValues.includes(cfg.category) ? cfg.category : ''
  const subOptions = category ? taxonomy.getLevelII(category) : []
  const subcategory = subOptions.includes(cfg.subcategory) ? cfg.subcategory : ''
  const typeOptions = category && subcategory ? taxonomy.getLevelIII(category, subcategory) : []
  const type = typeOptions.includes(cfg.type) ? cfg.type : ''
  return { ...cfg, category, subcategory, type }
}

interface AddNodePanelV2Props {
  isOpen: boolean
  onClose: () => void
  onConfigChange: (config: PanelConfig) => void
  /** ENG-2631: the persisted last selection. The panel initializes from it so the
   *  shape + classification survive a close/reopen (e.g. after the Details panel's X). */
  initialConfig?: PanelConfig
}

export default function AddNodePanelV2({ isOpen, onClose, onConfigChange, initialConfig = EMPTY_CONFIG }: AddNodePanelV2Props) {
  const { t } = useTranslation()
  const taxonomy = useTaxonomy()
  const initialSeed = sanitizeConfig(initialConfig, taxonomy)
  const [showTips, setShowTips] = useState(true)
  const [category, setCategory] = useState(initialSeed.category)
  const [subcategory, setSubcategory] = useState(initialSeed.subcategory)
  const [type, setType] = useState(initialSeed.type)
  const [label, setLabel] = useState(initialSeed.label)
  const [note, setNote] = useState(initialSeed.note)
  const [annotationText, setAnnotationText] = useState(initialSeed.annotationText)
  const [shape, setShape] = useState<'rectangle' | 'polygon' | 'line' | 'point'>(initialSeed.shape)

  // Use the canonical PIC001 taxonomy (single source of truth), matching the V1
  // panel (DetailsFlyout) and the edit panel (NodeDetailsPanelV2). Sourcing from
  // the static config (not the symbol-name-prefix `classRecords`, which exposed
  // legacy duplicates and never populated the cascade) gives every user the same
  // list and keeps saved pnid_level_* values canonical, with the subcat/type cascade.
  //
  // ENG-2614: Category/Subcategory/Type are INDEPENDENT of the chosen Shape. Shape
  // is the capture geometry (rectangle / polygon / line); classification is separate
  // ("draw first, then classify"). The full taxonomy is always offered — picking a
  // shape never filters the options or clears the current classification.
  const categoryOptions = taxonomy.levelIValues
  const subcategoryOptions = useMemo(
    () => (category ? taxonomy.getLevelII(category) : []),
    [category, taxonomy],
  )
  const typeOptions = useMemo(
    () => (category && subcategory ? taxonomy.getLevelIII(category, subcategory) : []),
    [category, subcategory, taxonomy],
  )

  // ENG-2631: seed the fields from the persisted selection whenever the panel OPENS,
  // and re-publish it so the parent's addNodeConfig + the canvas draw tool match what
  // the panel shows. This used to reset to empty/Rectangle on every mount, which wiped
  // the annotator's shape + classification each time the panel reopened (e.g. after
  // clicking the Details panel's X and coming back).
  //
  // Keyed on the open transition (not mount-once) so a caller that keeps the panel
  // mounted and merely toggles `isOpen` still picks up the LATEST persisted config
  // rather than the value captured at first mount. The refs let the effect read the
  // newest props on open without re-running (and clobbering in-progress edits) every
  // time the parent mirrors a keystroke back into `initialConfig`.
  const onConfigChangeRef = useRef(onConfigChange)
  onConfigChangeRef.current = onConfigChange
  const initialConfigRef = useRef(initialConfig)
  initialConfigRef.current = initialConfig
  const wasOpenRef = useRef(false)
  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false
      return
    }
    if (wasOpenRef.current) return // already open — don't overwrite the user's edits
    wasOpenRef.current = true
    const seed = sanitizeConfig(initialConfigRef.current, taxonomy)
    setCategory(seed.category)
    setSubcategory(seed.subcategory)
    setType(seed.type)
    setLabel(seed.label)
    setNote(seed.note)
    setAnnotationText(seed.annotationText)
    setShape(seed.shape)
    onConfigChangeRef.current(seed)
  }, [isOpen])

  const handleChange = (field: string, value: string) => {
    const next = { category, subcategory, type, label, note, annotationText, [field]: value }
    if (field === 'category') {
      // Reset dependent levels when the parent category changes.
      setCategory(value); setSubcategory(''); setType('')
      next.subcategory = ''; next.type = ''
    } else if (field === 'subcategory') {
      setSubcategory(value); setType('')
      next.type = ''
    } else if (field === 'type') { setType(value) }
    else if (field === 'label') { setLabel(value) }
    else if (field === 'note') { setNote(value) }
    else if (field === 'annotationText') { setAnnotationText(value) }
    onConfigChange({
      category: next.category,
      subcategory: next.subcategory,
      type: next.type,
      label: next.label,
      note: next.note,
      annotationText: next.annotationText,
      shape,
    })
  }

  // Shape toggle: rectangle drag, freeform polygon trace, or polyline. ENG-2614:
  // shape is decoupled from classification, so switching it only changes the
  // capture geometry and leaves the chosen Category/Subcategory/Type untouched.
  const handleShapeChange = (s: 'rectangle' | 'polygon' | 'line' | 'point') => {
    setShape(s)
    // A junction point IS a Signal Tee, so picking it pre-fills the
    // Instrumentation → Line → Signal Tee classification — that's what makes a
    // placed T a complete, valid Signal Tee without a second trip through the
    // dropdowns. This is a DEFAULT, not a constraint (ENG-2614): every taxonomy
    // option stays available and the user can change it. Any other shape leaves
    // the current classification untouched.
    //
    // P&ID only: "Signal Tee" is a leaf of that sheet and does not exist in the
    // SLD taxonomy, so pre-filling it there would seed a classification the
    // dropdowns cannot display and `sanitizeConfig` would silently strip. On a
    // non-P&ID drawing a point is placed unclassified, like any other shape.
    if (s === 'point' && taxonomy.discipline === 'pnid') {
      setCategory('Instrumentation'); setSubcategory('Line'); setType('Signal Tee')
      onConfigChange({ category: 'Instrumentation', subcategory: 'Line', type: 'Signal Tee', label, note, annotationText, shape: s })
      return
    }
    onConfigChange({ category, subcategory, type, label, note, annotationText, shape: s })
  }

  // Required-field validation: a required-but-empty field gets a red outline (mirrors the
  // Approve gate). Category is always required; Subcategory + Type are required except for
  // Annotation (Category only); Type only when one is selectable.
  // "Tee (junction)" is P&ID vocabulary — the point shape places a Signal Tee
  // there. An SLD has no signal tees: the same dot is a NODE (a busbar/branch
  // point), so it is named for what it is on the sheet being annotated.
  const isSld = taxonomy.discipline === 'sld'

  const subcategoryRequired = category !== taxonomy.annotationLevelI
  const typeRequired = subcategoryRequired && typeOptions.length > 0
  const subcategoryMissing = subcategoryRequired && !!category && !subcategory
  const typeMissing = typeRequired && !type
  const fieldClass = (missing: boolean) =>
    `w-full h-9 px-3 rounded-md border bg-white dark:bg-dark-card text-sm text-foreground disabled:opacity-50 disabled:cursor-not-allowed ${missing ? 'border-red-500 ring-1 ring-red-500' : 'border-[#e5e5e5] dark:border-dark-border'}`

  if (!isOpen) return null

  return (
    <div className="w-[391px] h-full border-l border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card flex flex-col shrink-0 overflow-hidden">
      {/* Header — the X backs out of the add flow and closes the panel. */}
      <div className="flex items-center justify-between px-6 py-4 shrink-0">
        <h3 className="text-base font-semibold text-foreground">
          {t('graph_editor:addNode.title', { defaultValue: 'Add Node' })}
        </h3>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('graph_editor:addNode.cancel', { defaultValue: 'Cancel' })}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Fields */}
      <div className="flex-1 overflow-y-auto px-6 pb-4">
        <div className="flex flex-col gap-5">
          {/* Shape: rectangle drag, freeform polygon trace, or polyline. */}
          <div>
            <label className="text-sm font-semibold text-foreground mb-1.5 block">{t('graph_editor:addNode.shape', { defaultValue: 'Shape' })}</label>
            <select
              value={shape}
              onChange={(e) => handleShapeChange(e.target.value as 'rectangle' | 'polygon' | 'line' | 'point')}
              className="w-full h-9 px-3 rounded-md border border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card text-sm text-foreground"
            >
              <option value="rectangle">{t('graph_editor:addNode.shapeRectangle', { defaultValue: 'Rectangle' })}</option>
              <option value="polygon">{t('graph_editor:addNode.shapePolygon', { defaultValue: 'Polygon' })}</option>
              <option value="line">{t('graph_editor:addNode.shapeLine', { defaultValue: 'Line' })}</option>
              <option value="point">
                {isSld
                  ? t('graph_editor:addNode.shapePointSld', { defaultValue: 'Node' })
                  : t('graph_editor:addNode.shapePoint', { defaultValue: 'Tee (junction)' })}
              </option>
            </select>
            {shape === 'polygon' && (
              <p className="text-xs text-muted-foreground mt-1.5">
                {t('graph_editor:addNode.shapePolygonHint', { defaultValue: 'Click to drop points along the edge; click the first point or double-click to close. Esc cancels.' })}
              </p>
            )}
            {shape === 'line' && (
              <p className="text-xs text-muted-foreground mt-1.5">
                {t('graph_editor:addNode.shapeLineHint', { defaultValue: 'Click a symbol to start, click to add bends, then click another symbol to connect — or double-click / Esc / Enter to finish. Backspace undoes a point.' })}
              </p>
            )}
            {shape === 'point' && (
              <p className="text-xs text-muted-foreground mt-1.5">
                {isSld
                  ? t('graph_editor:addNode.shapePointHintSld', { defaultValue: 'Click to place a node (a small dot where lines meet). Lines snap to it.' })
                  : t('graph_editor:addNode.shapePointHint', { defaultValue: 'Click to place a signal-tee junction (a small dot where lines meet). Lines snap to it.' })}
              </p>
            )}
            {/* ENG-2606: surface how to pan while a draw tool is active — for ANY shape,
                the canvas can be repositioned without leaving the tool (annotators often
                don't discover this). Always shown in the Add-Object flow. */}
            <p className="text-xs text-muted-foreground mt-1.5">
              {t('graph_editor:addNode.shapePanHint', { defaultValue: 'Tip: hold Space and drag to pan around while drawing.' })}
            </p>
          </div>

          {/* Category */}
          <div>
            <label className="text-sm font-semibold text-foreground mb-1.5 block">{t('graph_editor:nodePanel.category', { defaultValue: 'Category' })}<span className="text-red-500"> *</span></label>
            <select
              value={category}
              onChange={(e) => handleChange('category', e.target.value)}
              className={fieldClass(false)}
            >
              <option value="">{t('graph_editor:nodePanel.selectCategory', { defaultValue: 'Select category' })}</option>
              {categoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {/* Subcategory */}
          <div>
            <label className="text-sm font-semibold text-foreground mb-1.5 block">{t('graph_editor:nodePanel.subcategory', { defaultValue: 'Subcategory' })}{subcategoryRequired && <span className="text-red-500"> *</span>}</label>
            <select
              value={subcategory}
              onChange={(e) => handleChange('subcategory', e.target.value)}
              disabled={!category}
              className={fieldClass(subcategoryMissing)}
            >
              <option value="">{t('graph_editor:nodePanel.selectSubcategory', { defaultValue: 'Select subcategory' })}</option>
              {subcategoryOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* Type */}
          <div>
            <label className="text-sm font-semibold text-foreground mb-1.5 block">{t('graph_editor:nodePanel.type', { defaultValue: 'Type' })}{typeRequired && <span className="text-red-500"> *</span>}</label>
            <select
              value={type}
              onChange={(e) => handleChange('type', e.target.value)}
              disabled={!category || !subcategory}
              className={fieldClass(typeMissing)}
            >
              <option value="">{t('graph_editor:nodePanel.selectType', { defaultValue: 'Select type' })}</option>
              {typeOptions.map(ty => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </div>

          {/* Tag */}
          <div>
            <label className="text-sm font-semibold text-foreground mb-1.5 block">{t('graph_editor:nodePanel.label', { defaultValue: 'Tag' })}</label>
            <input
              type="text"
              value={label}
              onChange={(e) => handleChange('label', e.target.value)}
              className="w-full h-9 px-3 rounded-md border border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card text-sm text-foreground"
            />
          </div>

          {/* Annotation Text — mirrors the Node Details panel so annotators can fill
              it BEFORE drawing (it used to only appear after the object existed). */}
          <div>
            <label className="text-sm font-semibold text-foreground mb-1.5 block">{t('graph_editor:nodePanel.annotationText', { defaultValue: 'Annotation Text' })}</label>
            <input
              type="text"
              value={annotationText}
              onChange={(e) => handleChange('annotationText', e.target.value)}
              className="w-full h-9 px-3 rounded-md border border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card text-sm text-foreground"
            />
          </div>

          {/* Note */}
          <div>
            <label className="text-sm font-semibold text-foreground mb-1.5 block">
              {t('graph_editor:nodePanel.note', { defaultValue: 'Note' })} <span className="font-normal text-muted-foreground">{t('graph_editor:nodePanel.optional', { defaultValue: '(Optional)' })}</span>
            </label>
            <textarea
              value={note}
              onChange={(e) => handleChange('note', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 rounded-md border border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card text-sm text-foreground resize-none"
            />
          </div>

          {/* Tips */}
          {showTips && (
            <div className="border border-[#e5e5e5] dark:border-dark-border rounded-lg p-4 relative">
              <button
                type="button"
                onClick={() => setShowTips(false)}
                className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
              <h4 className="text-sm font-semibold text-foreground mb-2">{t('graph_editor:addNode.tips', { defaultValue: 'Tips' })}</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>{t('graph_editor:addNode.tipClickCanvas', { defaultValue: '• Click the canvas to add' })}</li>
                <li>{t('graph_editor:addNode.tipChosenSettings', { defaultValue: '• Node uses the chosen settings' })}</li>
                <li>{t('graph_editor:addNode.tipEditAfter', { defaultValue: '• Edit the node after it appears' })}</li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
